/**
 * FuneralServicesDirectory — cross-border listing of funeral homes,
 * crematoriums, repatriation specialists, monument makers, priests,
 * embalmers, transport and digital-memorial publishers.
 *
 * Public route `/funeral-services` — surfaces on Social Pulse, IFT and
 * MyFamilyTree.care under Obituaries / Memorials / Family Tree.
 * Server: `GET /api/funeral-services` + `/funeral-services/meta/facets`.
 */
import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { http } from "@/lib/api";
import SeoHead from "@/components/SeoHead";
import {
  Building2, Phone, Globe, Mail, Search, MapPin, ShieldCheck, ArrowLeft,
  MessageCircle,
} from "lucide-react";

const KIND_LABELS = {
  funeral_home:     "Funeral Home",
  crematorium:      "Crematorium",
  cemetery_operator:"Cemetery Operator",
  embalming:        "Embalming",
  transport:        "Transport",
  repatriation:     "Repatriation",
  memorial_planner: "Memorial Planner",
  floral:           "Floral",
  catering:         "Catering",
  clergy:           "Clergy / Priest",
  grief_counseling: "Grief Counseling",
  monument_maker:   "Monument Maker",
  livestream:       "Livestream",
  digital_memorial: "Digital Memorial",
};

function ServiceCard({ s }) {
  return (
    <Link
      to={`/funeral-services/${s.slug}`}
      className="wofy-card p-4 hover:shadow-md transition block"
      style={{ borderColor: "var(--wofy-border)" }}
      data-testid={`funeral-service-card-${s.slug}`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="font-display text-lg leading-tight truncate">{s.name}</div>
          <div className="text-xs opacity-70 mt-0.5">
            {KIND_LABELS[s.service_kind] || s.service_kind}
            {s.religions?.length && s.religions[0] !== "all" ? ` · ${s.religions.slice(0, 3).join(" · ")}` : ""}
          </div>
        </div>
        {s.verified && (
          <span
            className="shrink-0 inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-mono uppercase"
            style={{ background: "#16A34A18", color: "#16A34A" }}
          >
            <ShieldCheck className="w-3 h-3" /> Verified
          </span>
        )}
      </div>
      <div className="mt-2 text-xs flex items-center gap-1" style={{ color: "var(--wofy-text-soft)" }}>
        <MapPin className="w-3 h-3" /> {[s.city, s.state, s.country].filter(Boolean).join(" · ")}
      </div>
      {s.summary && <p className="mt-2 text-xs leading-relaxed line-clamp-3">{s.summary}</p>}
      <div className="mt-3 text-[11px] font-mono flex items-center gap-3 opacity-80 flex-wrap">
        {s.phone     && <span className="inline-flex items-center gap-1"><Phone className="w-3 h-3" /> {s.phone}</span>}
        {s.whatsapp  && <span className="inline-flex items-center gap-1"><MessageCircle className="w-3 h-3" /> WhatsApp</span>}
        {s.website   && <span className="inline-flex items-center gap-1"><Globe className="w-3 h-3" /> Web</span>}
      </div>
    </Link>
  );
}


export function FuneralServicesDirectory() {
  const [items, setItems]     = useState([]);
  const [total, setTotal]     = useState(0);
  const [loading, setLoading] = useState(true);
  const [q, setQ]             = useState("");
  const [kind, setKind]       = useState("");
  const [country, setCountry] = useState("");
  const [religion, setReligion] = useState("");
  const [facets, setFacets]   = useState({ kinds: [], geo: [], total: 0 });

  useEffect(() => {
    http.get("/funeral-services/meta/facets")
      .then(({ data }) => setFacets(data))
      .catch(() => {});
  }, []);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    const p = new URLSearchParams();
    if (q.trim())    p.set("q", q.trim());
    if (kind)        p.set("kind", kind);
    if (country)     p.set("country", country);
    if (religion)    p.set("religion", religion);
    p.set("limit", "60");
    http.get(`/funeral-services?${p}`)
      .then(({ data }) => { if (!alive) return; setItems(data.items || []); setTotal(data.total || 0); })
      .catch(() => alive && setItems([]))
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [q, kind, country, religion]);

  const countries = useMemo(() => {
    const map = new Map();
    for (const g of facets.geo || []) map.set(g.country, (map.get(g.country) || 0) + g.count);
    return Array.from(map.entries()).sort((a, b) => b[1] - a[1]);
  }, [facets]);

  return (
    <div className="max-w-6xl mx-auto px-4 lg:px-8 py-8" data-testid="funeral-services-directory">
      <SeoHead
        title="Funeral Services Directory · India + Diaspora"
        description="Find funeral homes, crematoriums, embalming, transport, repatriation, priests, floral, monument makers and grief support across India, the US, UK, Gulf and Australia."
      />
      <div className="overline mb-2" style={{ color: "var(--wofy-primary)" }}>§ FUNERAL SERVICES DIRECTORY</div>
      <h1 className="font-display text-4xl md:text-5xl leading-tight">Find help — funeral homes &amp; services</h1>
      <p className="mt-3 text-lg max-w-3xl" style={{ color: "var(--wofy-text-soft)" }}>
        {total.toLocaleString()} providers across {countries.length} countries. Where such directories are rare in India,
        this listing brings them together — funeral homes, crematoriums, priests, transport, repatriation, embalming,
        monument makers and grief support.
      </p>
      <p className="mt-2 text-sm">
        <Link
          to="/articles/indian-funeral-traditions-and-memorial-practices"
          className="underline"
          style={{ color: "var(--wofy-primary)" }}
        >
          Read: Indian Funeral Traditions &amp; Memorial Practices →
        </Link>
        {" · "}
        <Link to="/cemeteries" className="underline" style={{ color: "var(--wofy-primary)" }}>
          Cemeteries Directory →
        </Link>
      </p>

      {/* Filters */}
      <div className="mt-6 wofy-card p-4" style={{ borderColor: "var(--wofy-border)" }}>
        <div className="flex flex-col md:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 opacity-60" />
            <input
              type="text"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search by name, city, service…"
              className="w-full pl-9 pr-3 py-2 rounded-lg border text-sm bg-transparent"
              style={{ borderColor: "var(--wofy-border)" }}
              data-testid="funeral-search"
            />
          </div>
          <select
            value={country}
            onChange={(e) => setCountry(e.target.value)}
            className="px-3 py-2 rounded-lg border text-sm bg-transparent"
            style={{ borderColor: "var(--wofy-border)" }}
            data-testid="funeral-filter-country"
          >
            <option value="">All countries</option>
            {countries.map(([n, c]) => <option key={n} value={n}>{n} ({c})</option>)}
          </select>
          <select
            value={religion}
            onChange={(e) => setReligion(e.target.value)}
            className="px-3 py-2 rounded-lg border text-sm bg-transparent"
            style={{ borderColor: "var(--wofy-border)" }}
            data-testid="funeral-filter-religion"
          >
            <option value="">All faiths</option>
            {["hindu", "christian", "muslim", "sikh", "buddhist", "jain", "parsi"].map((r) => <option key={r} value={r}>{r}</option>)}
          </select>
        </div>
        {/* Kind chips */}
        <div className="mt-3 flex flex-wrap gap-1.5" data-testid="funeral-kind-chips">
          <button
            type="button"
            onClick={() => setKind("")}
            className={`rounded-full border px-3 py-1 text-xs ${kind === "" ? "bg-slate-900 text-white" : ""}`}
            style={{ borderColor: "var(--wofy-border)" }}
          >
            All ({facets.total})
          </button>
          {(facets.kinds || []).map((k) => (
            <button
              key={k.kind}
              type="button"
              onClick={() => setKind(kind === k.kind ? "" : k.kind)}
              className={`rounded-full border px-3 py-1 text-xs ${kind === k.kind ? "bg-slate-900 text-white" : ""}`}
              style={{ borderColor: "var(--wofy-border)" }}
              data-testid={`funeral-kind-${k.kind}`}
            >
              {KIND_LABELS[k.kind] || k.kind} ({k.count})
            </button>
          ))}
        </div>
      </div>

      {/* Results */}
      <div className="mt-6">
        {loading && <div className="text-sm opacity-70">Loading…</div>}
        {!loading && items.length === 0 && <div className="text-sm opacity-70">No providers match those filters.</div>}
        {!loading && items.length > 0 && (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {items.map((s) => <ServiceCard key={s.slug} s={s} />)}
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * FuneralServiceDetail — one provider's full page.
 * Route: `/funeral-services/:slug`.
 */
export function FuneralServiceDetail() {
  const { slug } = useParams();
  const [s, setS] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    http.get(`/funeral-services/${encodeURIComponent(slug)}`)
      .then(({ data }) => setS(data))
      .catch((e) => setError(e?.response?.data?.detail || "Not found"));
  }, [slug]);

  if (error) return (
    <div className="max-w-4xl mx-auto py-12 px-6">
      <Link to="/funeral-services" className="text-sm underline"><ArrowLeft className="inline w-3 h-3" /> Back</Link>
      <div className="mt-4 text-sm opacity-70">{error}</div>
    </div>
  );
  if (!s) return <div className="max-w-4xl mx-auto py-12 px-6 text-sm opacity-70">Loading…</div>;

  return (
    <div className="max-w-4xl mx-auto py-8 px-4 lg:px-8" data-testid="funeral-service-detail">
      <SeoHead title={`${s.name} · Funeral Services`} description={s.summary} />
      <Link to="/funeral-services" className="text-xs opacity-70 hover:opacity-100">
        <ArrowLeft className="inline w-3 h-3" /> Back to directory
      </Link>
      <div className="overline mt-3" style={{ color: "var(--wofy-primary)" }}>
        {KIND_LABELS[s.service_kind] || s.service_kind}
      </div>
      <h1 className="font-display text-4xl md:text-5xl mt-1 leading-tight">{s.name}</h1>

      {/* Contact card */}
      <div className="mt-4 wofy-card p-4" style={{ borderColor: "var(--wofy-border)" }}>
        <div className="flex flex-wrap items-center gap-4 text-sm">
          {s.phone && (
            <a href={`tel:${s.phone}`} className="inline-flex items-center gap-1 hover:underline" data-testid="funeral-detail-phone">
              <Phone className="w-4 h-4" /> {s.phone}
            </a>
          )}
          {s.whatsapp && (
            <a href={`https://wa.me/${s.whatsapp.replace(/[^0-9]/g, "")}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 hover:underline">
              <MessageCircle className="w-4 h-4" /> WhatsApp
            </a>
          )}
          {s.email && (
            <a href={`mailto:${s.email}`} className="inline-flex items-center gap-1 hover:underline">
              <Mail className="w-4 h-4" /> {s.email}
            </a>
          )}
          {s.website && (
            <a href={s.website} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 hover:underline">
              <Globe className="w-4 h-4" /> Website
            </a>
          )}
        </div>
        <div className="mt-2 text-xs" style={{ color: "var(--wofy-text-soft)" }}>
          <MapPin className="inline w-3 h-3" /> {[s.address, s.city, s.state, s.country].filter(Boolean).join(" · ")}
        </div>
      </div>

      {s.summary && <p className="mt-6 text-lg leading-relaxed">{s.summary}</p>}

      {s.services_offered?.length > 0 && (
        <div className="mt-6">
          <div className="overline mb-2">SERVICES OFFERED</div>
          <ul className="list-disc pl-5 text-sm space-y-1">
            {s.services_offered.map((x, i) => <li key={i}>{x}</li>)}
          </ul>
        </div>
      )}
      {s.religions?.length > 0 && (
        <div className="mt-4 text-xs" style={{ color: "var(--wofy-text-soft)" }}>
          <b>Communities served:</b> {s.religions.join(", ")}
        </div>
      )}
      {s.languages?.length > 0 && (
        <div className="mt-1 text-xs" style={{ color: "var(--wofy-text-soft)" }}>
          <b>Languages:</b> {s.languages.join(", ")}
        </div>
      )}

      <div className="mt-8 text-xs" style={{ color: "var(--wofy-text-soft)" }}>
        Traditions and rites vary. Read our editorial guide:&nbsp;
        <Link to="/articles/indian-funeral-traditions-and-memorial-practices" className="underline" style={{ color: "var(--wofy-primary)" }}>
          Indian Funeral Traditions →
        </Link>
      </div>
    </div>
  );
}
